<script setup lang="ts">
import { useLocale } from '../../composables/useLocale';

const { t } = useLocale();

defineProps({
  name: {
    type: String,
    required: true,
  },
  userName: {
    type: String,
    required: true,
  },
  appLogoImgs: {
    type: Array<string>, // Array images
    required: true,
  },
  appUrl: {
    type: String,
    required: true,
  },
  password: {
    type: String,
    default: '',
    required: false,
  },
  qrCodeImage: {
    type: String, // QR code image data urls
    default: '',
    required: false,
  },
  qrCodeLoginUrl: {
    type: String, // QR code login url
    default: '',
    required: false,
  },
  columns: {
    type: Number, // 3 cards per row by default, 2 when long usernames/passwords would otherwise overflow
    default: 3,
  },
});
</script>

<template>
  <div class="login_card" :class="columns === 2 ? 'login_card--cols-2' : 'login_card--cols-3'">
    <div class="login_card-header">
      <div class="login_card-name">
        {{ name }}
      </div>
    </div>
    <div v-if="qrCodeImage" class="login_card-info-conatiner">
      <a :href="qrCodeLoginUrl" target="_blank" rel="noopener noreferrer">
        <img :src="qrCodeImage" alt="QR Code" width="124" height="124" />
      </a>
    </div>
    <div v-if="userName" class="login_card-info-conatiner">
      <div class="login_card-label">{{ t('lcp.username') }}</div>
      <div class="login_card-chip" small>
        {{ userName }}
      </div>
    </div>
    <div v-if="password" class="login_card-info-conatiner">
      <div class="login_card-label">{{ t('lcp.password') }}</div>
      <div class="login_card-chip" small>
        {{ password }}
      </div>
    </div>
    <div class="login_card-footer">
      <div class="login_card-app-logos">
        <img
          v-for="logo in appLogoImgs"
          :key="logo"
          class="login_card-logo"
          :src="logo"
          alt="App Logo"
          width="28"
          height="28"
        />
      </div>
      <span class="login_card-app-url">
        {{ appUrl }}
      </span>
    </div>
  </div>
</template>

<style lang="css">
@font-face {
  font-family: 'filson-soft';
  src: local('filson-soft'), url('../../../assets/fonts/Mostardesign - FilsonSoftMedium.otf');
}

@font-face {
  font-family: 'Proxima-Soft';
  src: local('proxima-soft'), url('../../../assets/fonts/Mark Simonson - Proxima Soft.otf');
}

.login_card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0 14px;
  background-color: white !important;
  color: #004e99;
}
.login_card--cols-3 {
  width: calc(209.3mm / 3) !important;
  height: calc(296.3mm / 3) !important;
}
.login_card--cols-2 {
  width: calc(209.3mm / 2) !important;
  height: calc(296.3mm / 3) !important;
}
.login_card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}
.login_card-name {
  font-size: 12px;
  font-family: 'filson-Soft';
  text-transform: capitalize;
}

.login_card-info-conatiner {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}
.login_card-label {
  font-family: 'Proxima-Soft';
  font-size: 13px;
  line-height: 13px;
  font-weight: 600;
  text-transform: capitalize;
  margin-bottom: 4px;
}
.login_card-chip {
  font-family: ui-monospace, Consolas, monospace;
  background-color: #f2f5f8;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.5px;
  padding: 6px 8px;
  border: thin solid #c9cccf;
  border-radius: 32px;
  stroke: 1px;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: center;
}
.login_card-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}
.login_card-app-logos {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.login_card-logo {
  width: 28px;
  height: 28px;
  margin: 0 4px;
}
.login_card-app-url {
  align-self: center;
  font-size: 8px;
  line-height: 8px;
  font-weight: 500;
  font-family: 'Proxima-Soft';
}
</style>
